import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';

import AddApplicantManuallyForm from '@features/applications/components/AddApplicantManuallyForm/AddApplicantManuallyForm';
import EnterpriseAddApplicantManuallyForm from '../EnterpriseAddApplicantManuallyForm/EnterpriseAddApplicantManuallyForm';
import { authContext } from '@staffedupusa/shared-components';
import { Close } from '@mui/icons-material';
import { AddPersonIcon } from '../../../../vectors/index';
import { selectedPlanName } from '@utils/pricing';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { track } from '@amplitude/analytics-browser';

type AddApplicantManuallyProps = {
  setTriggerChange: Dispatch<SetStateAction<boolean>>;
  handlePageNo: (newPageNo: number) => void;
};

const AddApplicantManually: FC<AddApplicantManuallyProps> = ({
  setTriggerChange,
  handlePageNo,
}) => {
  const { authData } = useContext(authContext);
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<
    'buttons' | 'form' | 'success'
  >('buttons');
  const [isHired, setIsHired] = useState(false);

  const handleAddApplicantClick = (hired: boolean) => {
    setModalContent('form');
    setIsHired(hired);
  };

  const getModalTitle = () => {
    if (modalContent === 'buttons') {
      return 'Add Applicant / New Hire';
    }

    if (isHired) {
      return 'Add New Hire';
    }

    return 'Add New Applicant';
  };

  const getModalContent = () => {
    switch (modalContent) {
      case 'buttons':
        return (
          <>
            <Button
              variant="contained"
              onClick={() => {
                track(
                  `${amplitudeEventsName.APPLICANTS_CLICK_ADD_APPLICANT_IN_MODAL}-${selectedPlan}`,
                );
                handleAddApplicantClick(false)
              }}
            >
              Add New Applicant
            </Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={() => {
                track(
                  `${amplitudeEventsName.APPLICANTS_CLICK_ADD_HIRE_IN_MODAL}-${selectedPlan}`,
                )
                handleAddApplicantClick(true)
              }}
            >
              Add New Hire
            </Button>
          </>
        );
      case 'form':
        return (
          <>
            {authData?.user?.company ? (
              <AddApplicantManuallyForm
                isHired={isHired}
                onSuccess={(data: { status: number }) => {
                  if (data && data?.status === 200) {
                    handlePageNo(1);
                    setTriggerChange(true);
                    setModalContent('success');
                  }
                }}
              />
            ) : (
              <EnterpriseAddApplicantManuallyForm
                isHired={isHired}
                onSuccess={(data: { status: number }) => {
                  if (data && data?.status === 200) {
                    handlePageNo(1);
                    setTriggerChange(true);
                    setModalContent('success');
                  }
                }}
              />
            )}
            <Button
              // color="info"
              variant="outlined"
              onClick={() => setModalContent('buttons')}
              // sx={{ alignSelf: 'center' }}
            >
              Back
            </Button>
          </>
        );
      case 'success':
        return (
          <>
            <Alert severity="success">{`New ${
              isHired ? 'Hire' : 'Applicant'
            } added!`}</Alert>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAddIcon />}
              onClick={() => setModalContent('buttons')}
            >
              Add Another
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        startIcon={<PersonAddIcon sx={{ fontSize: '12px !important', }} />}
        onClick={() => {
          track(
            `${amplitudeEventsName.APPLICANTS_CLICK_ADD_APPLICANT}-${selectedPlan}`,
          );
          setIsModalOpen(true);
        }}
        color="purple"
        sx={{
          width: { xs: "100%" },
          fontSize: '12px',
          padding: '5px !important'
          // whiteSpace: 'nowrap',
        }}
      >
        Add Applicant / Hire
      </Button>
      <Dialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setModalContent('buttons');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={() => {
              setIsModalOpen(false);
              setModalContent('buttons');
            }}
          >
            <Close />
          </IconButton>
          <br />
          <AddPersonIcon />
          <Typography variant="h4" sx={{ pt: 3 }}>
            {getModalTitle()}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack sx={{ marginBottom: 2 }} spacing={4}>
            {getModalContent()}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddApplicantManually;
